/**
 * Inventory schema — vehicles table, 8 pgEnums, Drizzle inferred types, and
 * the canonical VehicleSchema Zod type.
 *
 * Consumers:
 *  - apps/web/lib/drizzle/ — withSite()-scoped vehicle queries
 *  - apps/web/app/admin/inventory/ — Server Actions validate against VehicleSchema
 *  - packages/feed-presets/ — column-mapping configs validated via VehicleSchema
 *  - apps/web/components/public/srp/ — SRP filter panel reads enum values
 *
 * The `features` column is a Postgres text[] constrained by VehicleSchema to
 * values present in the feature taxonomy (packages/schema/src/feature-taxonomy.ts).
 * Free-form notes go to `customFeatures` (plain text).
 *
 * Every row is site-scoped: site_id FK with onDelete "cascade". All queries
 * must pass through withSite() in lib/drizzle/with-site.ts.
 */

import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { z } from "zod";
import { isValidFeature } from "./feature-taxonomy";
import { sites } from "./tables";
import type { DealerAddonOverrides } from "./dealer-addons";
import { DealerAddonOverridesSchema } from "./dealer-addons";

/**
 * One uploaded photo, post-registration with Cloudflare Images.
 *
 * - `id`: stable client-side identifier (ULID) — survives delete/reorder.
 * - `cdnId`: Cloudflare Images object ID; combine with the delivery host
 *   and a variant name to render: `https://<host>/<accountHash>/<cdnId>/<variant>`.
 * - `originalKey`: R2 object key under bucket `R2_BUCKET`, used for
 *   later cleanup or watermark-redo. Shape: `<siteId>/<vehicleId>/<photoId>.<ext>`.
 * - `watermarkedAt`: ISO timestamp of the latest watermark pass; null if
 *   never watermarked. Used by the Inngest finalize step to decide whether
 *   a re-watermark is needed (e.g. after a Reseller logo change).
 */
export type VehiclePhoto = {
  id: string;
  cdnId: string;
  originalKey: string;
  watermarkedAt: string | null;
  sourceUrl?: string; // origin URL when feed-sourced
  sourceUrlHash?: string; // SHA-1 of sourceUrl for fast dedup lookup
};

// ----------------------------------------------------------------------------
// Enums
// ----------------------------------------------------------------------------

export const bodyStyleEnum = pgEnum("body_style", [
  "sedan",
  "suv",
  "truck",
  "coupe",
  "convertible",
  "hatchback",
  "wagon",
  "van",
  "minivan",
  "other",
]);

export const fuelTypeEnum = pgEnum("fuel_type", [
  "gas",
  "diesel",
  "electric",
  "hybrid",
  "plug-in-hybrid",
  "other",
]);

export const drivetrainEnum = pgEnum("drivetrain", ["fwd", "rwd", "awd", "4wd"]);

export const transmissionEnum = pgEnum("transmission", ["automatic", "manual", "cvt", "other"]);

export const vehicleConditionEnum = pgEnum("vehicle_condition", [
  "new",
  "used",
  "certified-pre-owned",
]);

export const vehicleStatusEnum = pgEnum("vehicle_status", [
  "available",
  "pending",
  "sold",
  "archived",
]);

export const vehicleSourceEnum = pgEnum("vehicle_source", ["manual", "feed", "import"]);

export const titleStatusEnum = pgEnum("title_status", [
  "clean",
  "salvage",
  "rebuilt",
  "flood",
  "lemon-law",
]);

// ----------------------------------------------------------------------------
// JSONB shape types
// ----------------------------------------------------------------------------

export type BuildSheet = {
  msrpCents: number | null;
  windowStickerUrl: string | null;
  priorityOptions: string[];
  standardEquipment: Array<{ category: string | null; description: string }>;
  optionalEquipment: Array<{
    category: string | null;
    description: string;
    msrpCents: number | null;
    code: string | null;
  }>;
  packages: Array<{
    name: string;
    code: string | null;
    description: string | null;
    msrpCents: number | null;
    items: string[];
  }>;
  optionCodes: string[];
};

export type TechSpecs = {
  engine?: {
    displacement?: string;
    description?: string;
    aspiration?:
      | "naturally-aspirated"
      | "turbocharged"
      | "supercharged"
      | "twin-turbo"
      | "electric";
    blockType?: string;
  };
  transmission?: { description?: string };
  dimensions?: { wheelbaseInches?: number };
  fuel?: {
    combinedMpg?: number;
    electric?: {
      rangeMiles?: number;
      batteryCapacityKwh?: number;
      batteryUsableKwh?: number;
      chargingPort?: "NACS" | "CCS1" | "CHAdeMO" | "J1772" | "Type2";
      dcFastPeakKw?: number;
      acPeakKw?: number;
      chargeTime10to80Minutes?: number;
      chargeTimeFullL2Hours?: number;
    };
  };
  identifiers?: {
    modelCode?: string;
    chromeStyleId?: string;
    trimDescription?: string;
  };
  interior?: { material?: string };
};

export type Warranty = {
  factory: { monthsRemaining?: number; milesRemaining?: number } | null;
  powertrain: { monthsRemaining?: number; milesRemaining?: number } | null;
  cpo: { provider?: string; months: number; miles: number } | null;
  extendedAvailable?: boolean;
};

export type Media = {
  videoUrl?: string;
  spinViewerUrl?: string;
  vehicleHistoryReportUrl?: string;
};

export type Compliance = {
  recalls: Array<{
    nhtsaCampaignNumber: string;
    component: string;
    summary: string;
    consequence: string;
    remedy: string;
    receivedDate: string;
  }>;
  recallsCheckedAt: string;
};

// ----------------------------------------------------------------------------
// Table
// ----------------------------------------------------------------------------

export const vehicles = pgTable(
  "vehicles",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),

    vin: text("vin").notNull(),
    stockNumber: text("stock_number"),
    slug: text("slug").notNull(),
    slugOverride: text("slug_override"),

    year: integer("year").notNull(),
    make: text("make").notNull(),
    model: text("model").notNull(),
    trim: text("trim"),
    bodyStyle: bodyStyleEnum("body_style").notNull(),
    fuelType: fuelTypeEnum("fuel_type"),
    drivetrain: drivetrainEnum("drivetrain"),
    transmission: transmissionEnum("transmission"),

    condition: vehicleConditionEnum("condition").notNull(),
    mileage: integer("mileage"),

    priceCents: integer("price_cents"),
    /**
     * Dealer's display MSRP — used for selling-price sorting/filtering.
     * Distinct from `vehicles.buildSheet.msrpCents` which is the factory
     * window-sticker MSRP from OEM data (read-only).
     */
    msrpCents: integer("msrp_cents"),

    exteriorColor: text("exterior_color"),
    interiorColor: text("interior_color"),

    features: text("features")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    customFeatures: text("custom_features"),
    description: text("description"),

    /**
     * Legacy URL list. Populated by:
     *  - feed/CSV imports (rows arrive with raw image URLs)
     *  - the wizard's manual paste flow before Slice B (kept as a fallback
     *    for users who'd rather link out)
     *  - mirror writes from the new upload pipeline (a `card`-variant URL
     *    is appended for every Cloudflare-Images-registered photo so any
     *    legacy reader still gets a working src)
     *
     * Source of truth for *uploaded* photos is `photos` below; `photoUrls`
     * is the derived/legacy view. Readers should prefer `photos[].cdnId`
     * (with a chosen variant) and fall back to `photoUrls` only when
     * `photos` is empty.
     */
    photoUrls: text("photo_urls")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),

    /**
     * Source of truth for photos uploaded via the Slice B pipeline. Each
     * entry is `{ id, cdnId, originalKey, watermarkedAt }`. List order is
     * display order. See `VehiclePhoto` for field semantics.
     */
    photos: jsonb("photos")
      .$type<VehiclePhoto[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),

    status: vehicleStatusEnum("status").notNull().default("available"),
    source: vehicleSourceEnum("source").notNull().default("manual"),
    sourceFeedId: uuid("source_feed_id"),
    sourceLastSyncedAt: timestamp("source_last_synced_at", { withTimezone: true }),

    // New-car-store data fidelity — first-class queryable columns
    doors: integer("doors"),
    cityMpg: integer("city_mpg"),
    highwayMpg: integer("highway_mpg"),
    engineCylinders: integer("engine_cylinders"),
    transmissionSpeed: integer("transmission_speed"),
    exteriorColorHex: text("exterior_color_hex"),
    titleStatus: titleStatusEnum("title_status").notNull().default("clean"),
    hasAccidentHistory: boolean("has_accident_history"),
    numberOfOwners: integer("number_of_owners"),
    acquiredDate: timestamp("acquired_date", { withTimezone: false, mode: "date" }),
    openRecallCount: integer("open_recall_count").notNull().default(0),

    // Rich VDP-rendered content — jsonb (default null/empty)
    buildSheet: jsonb("build_sheet").$type<BuildSheet>(),
    techSpecs: jsonb("tech_specs").$type<TechSpecs>(),
    warranty: jsonb("warranty").$type<Warranty>(),
    media: jsonb("media").$type<Media>(),
    compliance: jsonb("compliance").$type<Compliance>(),
    dealerAddonOverrides: jsonb("dealer_addon_overrides").$type<DealerAddonOverrides>(),

    seoTitle: text("seo_title"),
    seoDescription: text("seo_description"),

    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteSlugUnique: uniqueIndex("vehicles_site_slug_idx").on(t.siteId, t.slug),
    siteVinIdx: index("vehicles_site_vin_idx").on(t.siteId, t.vin),
    siteStockIdx: index("vehicles_site_stock_idx").on(t.siteId, t.stockNumber),
    siteStatusIdx: index("vehicles_site_status_idx").on(t.siteId, t.status),
    siteMakeIdx: index("vehicles_site_make_idx").on(t.siteId, t.make),
    siteModelIdx: index("vehicles_site_model_idx").on(t.siteId, t.model),
    siteYearIdx: index("vehicles_site_year_idx").on(t.siteId, t.year),
    sitePriceIdx: index("vehicles_site_price_idx").on(t.siteId, t.priceCents),
    siteMileageIdx: index("vehicles_site_mileage_idx").on(t.siteId, t.mileage),
    siteBodyIdx: index("vehicles_site_body_idx").on(t.siteId, t.bodyStyle),
    siteConditionIdx: index("vehicles_site_condition_idx").on(t.siteId, t.condition),
    featuresGinIdx: index("vehicles_features_gin_idx").using("gin", t.features),
    siteDoorsIdx: index("vehicles_site_doors_idx").on(t.siteId, t.doors),
    siteCityMpgIdx: index("vehicles_site_city_mpg_idx").on(t.siteId, t.cityMpg),
    siteHighwayMpgIdx: index("vehicles_site_highway_mpg_idx").on(t.siteId, t.highwayMpg),
    siteCylindersIdx: index("vehicles_site_cylinders_idx").on(t.siteId, t.engineCylinders),
    siteTitleStatusIdx: index("vehicles_site_title_status_idx").on(t.siteId, t.titleStatus),
    siteAcquiredIdx: index("vehicles_site_acquired_idx").on(t.siteId, t.acquiredDate),
    siteRecallCountIdx: index("vehicles_site_recall_count_idx").on(t.siteId, t.openRecallCount),
  }),
);

// ----------------------------------------------------------------------------
// Inferred types
// ----------------------------------------------------------------------------

export type Vehicle = typeof vehicles.$inferSelect;
export type NewVehicle = typeof vehicles.$inferInsert;

// ----------------------------------------------------------------------------
// Zod canonical type — Server Action validation + feed-adapter mapping
// ----------------------------------------------------------------------------

export const BuildSheetSchema = z.object({
  msrpCents: z.number().int().nullable(),
  windowStickerUrl: z.string().url().nullable(),
  priorityOptions: z.array(z.string()).default([]),
  standardEquipment: z
    .array(
      z.object({
        category: z.string().nullable(),
        description: z.string(),
      }),
    )
    .default([]),
  optionalEquipment: z
    .array(
      z.object({
        category: z.string().nullable(),
        description: z.string(),
        msrpCents: z.number().int().nullable(),
        code: z.string().nullable(),
      }),
    )
    .default([]),
  packages: z
    .array(
      z.object({
        name: z.string(),
        code: z.string().nullable(),
        description: z.string().nullable(),
        msrpCents: z.number().int().nullable(),
        items: z.array(z.string()).default([]),
      }),
    )
    .default([]),
  optionCodes: z.array(z.string()).default([]),
});

export const TechSpecsSchema = z.object({
  engine: z
    .object({
      displacement: z.string().optional(),
      description: z.string().optional(),
      aspiration: z
        .enum(["naturally-aspirated", "turbocharged", "supercharged", "twin-turbo", "electric"])
        .optional(),
      blockType: z.string().optional(),
    })
    .optional(),
  transmission: z.object({ description: z.string().optional() }).optional(),
  dimensions: z.object({ wheelbaseInches: z.number().optional() }).optional(),
  fuel: z
    .object({
      combinedMpg: z.number().optional(),
      electric: z
        .object({
          rangeMiles: z.number().optional(),
          batteryCapacityKwh: z.number().optional(),
          batteryUsableKwh: z.number().optional(),
          chargingPort: z.enum(["NACS", "CCS1", "CHAdeMO", "J1772", "Type2"]).optional(),
          dcFastPeakKw: z.number().optional(),
          acPeakKw: z.number().optional(),
          chargeTime10to80Minutes: z.number().optional(),
          chargeTimeFullL2Hours: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
  identifiers: z
    .object({
      modelCode: z.string().optional(),
      chromeStyleId: z.string().optional(),
      trimDescription: z.string().optional(),
    })
    .optional(),
  interior: z.object({ material: z.string().optional() }).optional(),
});

export const WarrantySchema = z.object({
  factory: z
    .object({
      monthsRemaining: z.number().int().optional(),
      milesRemaining: z.number().int().optional(),
    })
    .nullable(),
  powertrain: z
    .object({
      monthsRemaining: z.number().int().optional(),
      milesRemaining: z.number().int().optional(),
    })
    .nullable(),
  cpo: z
    .object({
      provider: z.string().optional(),
      months: z.number().int(),
      miles: z.number().int(),
    })
    .nullable(),
  extendedAvailable: z.boolean().optional(),
});

export const MediaSchema = z.object({
  videoUrl: z.string().url().optional(),
  spinViewerUrl: z.string().url().optional(),
  vehicleHistoryReportUrl: z.string().url().optional(),
});

export const ComplianceSchema = z.object({
  recalls: z
    .array(
      z.object({
        nhtsaCampaignNumber: z.string(),
        component: z.string(),
        summary: z.string(),
        consequence: z.string(),
        remedy: z.string(),
        receivedDate: z.string(),
      }),
    )
    .default([]),
  recallsCheckedAt: z.string().datetime(),
});

/**
 * User-supplied Vehicle shape — what the manual editor / feed adapters / CSV
 * import provide. Excludes `id`, `siteId`, `createdAt`, `updatedAt` (server-set
 * at the persistence layer). Callers merge `siteId` + this parsed input when
 * inserting via Drizzle.
 */
export const VehicleSchema = z.object({
  vin: z.string().length(17),
  stockNumber: z.string().nullable(),
  slug: z.string().min(1),
  slugOverride: z.string().nullable(),
  year: z.number().int().min(1900).max(2100),
  make: z.string().min(1).max(80),
  model: z.string().min(1).max(80),
  trim: z.string().nullable(),
  bodyStyle: z.enum([
    "sedan",
    "suv",
    "truck",
    "coupe",
    "convertible",
    "hatchback",
    "wagon",
    "van",
    "minivan",
    "other",
  ]),
  fuelType: z.enum(["gas", "diesel", "electric", "hybrid", "plug-in-hybrid", "other"]).nullable(),
  drivetrain: z.enum(["fwd", "rwd", "awd", "4wd"]).nullable(),
  transmission: z.enum(["automatic", "manual", "cvt", "other"]).nullable(),
  condition: z.enum(["new", "used", "certified-pre-owned"]),
  mileage: z.number().int().min(0).nullable(),
  priceCents: z.number().int().min(0).nullable(),
  msrpCents: z.number().int().min(0).nullable(),
  exteriorColor: z.string().nullable(),
  interiorColor: z.string().nullable(),
  features: z
    .array(
      z.string().refine(isValidFeature, {
        message: "feature value not in taxonomy",
      }),
    )
    .default([]),
  customFeatures: z.string().nullable(),
  description: z.string().nullable(),
  photoUrls: z.array(z.string().url()).default([]),
  photos: z
    .array(
      z.object({
        id: z.string().min(1),
        cdnId: z.string().min(1),
        originalKey: z.string().min(1),
        watermarkedAt: z.string().nullable(),
        sourceUrl: z.string().optional(),
        sourceUrlHash: z.string().optional(),
      }),
    )
    .default([]),
  status: z.enum(["available", "pending", "sold", "archived"]).default("available"),
  source: z.enum(["manual", "feed", "import"]).default("manual"),
  doors: z.number().int().min(2).max(6).nullable(),
  cityMpg: z.number().int().min(0).max(200).nullable(),
  highwayMpg: z.number().int().min(0).max(200).nullable(),
  engineCylinders: z.number().int().min(0).max(16).nullable(),
  transmissionSpeed: z.number().int().min(0).max(12).nullable(),
  exteriorColorHex: z
    .string()
    .regex(/^[0-9A-F]{6}$/i)
    .nullable(),
  titleStatus: z.enum(["clean", "salvage", "rebuilt", "flood", "lemon-law"]).default("clean"),
  hasAccidentHistory: z.boolean().nullable(),
  numberOfOwners: z.number().int().min(0).max(20).nullable(),
  acquiredDate: z.coerce.date().nullable(),
  openRecallCount: z.number().int().min(0).default(0),
  buildSheet: BuildSheetSchema.nullable().default(null),
  techSpecs: TechSpecsSchema.nullable().default(null),
  warranty: WarrantySchema.nullable().default(null),
  media: MediaSchema.nullable().default(null),
  compliance: ComplianceSchema.nullable().default(null),
  dealerAddonOverrides: DealerAddonOverridesSchema.nullable().default(null),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
});

export type VehicleInput = z.infer<typeof VehicleSchema>;
